import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useSignedInContext } from "../auth/useSignedInContext";
import type { SourceSummary, SourceType, Collection } from "./types";

const TYPE_LABELS: Record<SourceType, string> = { pdf: "PDF", article: "Artikel", note: "Notiz", podcast: "Transkript" };

export function LibraryPage() {
  const { member } = useSignedInContext();
  const [search, setSearch] = useState("");
  const [type, setType] = useState<SourceType | "">("");
  const [collectionId, setCollectionId] = useState("");
  const [collections, setCollections] = useState<Collection[]>([]);
  const [items, setItems] = useState<SourceSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.get<{ collections: Collection[] }>("/api/v1/collections").then((r) => setCollections(r.collections));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (search.trim()) params.set("search", search.trim());
    if (type) params.set("type", type);
    if (collectionId) params.set("collectionId", collectionId);
    const timeout = setTimeout(() => {
      api
        .get<{ sources: SourceSummary[] }>(`/api/v1/sources?${params.toString()}`)
        .then((r) => setItems(r.sources))
        .catch(() => setError("Bibliothek konnte nicht geladen werden."));
    }, 200);
    return () => clearTimeout(timeout);
  }, [search, type, collectionId]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <section className="pw-card">
        <h1 style={{ margin: "0 0 0.8rem", fontSize: "1.35rem", fontWeight: 600 }}>Bibliothek</h1>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <input
            type="text"
            placeholder="Titel oder Autor suchen …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              flex: 1,
              minWidth: "220px",
              padding: "0.6rem 0.8rem",
              borderRadius: "var(--pw-radius-sm)",
              border: "1px solid var(--pw-color-surface-border)",
              background: "var(--pw-color-surface)",
              color: "var(--pw-color-text)"
            }}
          />
          <select
            value={type}
            onChange={(e) => setType(e.target.value as SourceType | "")}
            style={{ padding: "0.6rem 0.8rem", borderRadius: "var(--pw-radius-sm)", background: "var(--pw-color-surface)", border: "1px solid var(--pw-color-surface-border)", color: "var(--pw-color-text)" }}
          >
            <option value="">Alle Typen</option>
            {(Object.keys(TYPE_LABELS) as SourceType[]).map((t) => (
              <option key={t} value={t}>
                {TYPE_LABELS[t]}
              </option>
            ))}
          </select>
          <select
            value={collectionId}
            onChange={(e) => setCollectionId(e.target.value)}
            style={{ padding: "0.6rem 0.8rem", borderRadius: "var(--pw-radius-sm)", background: "var(--pw-color-surface)", border: "1px solid var(--pw-color-surface-border)", color: "var(--pw-color-text)" }}
          >
            <option value="">Alle Sammlungen</option>
            {collections.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </section>

      {error && <div className="pw-alert pw-alert--error">{error}</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
        {items?.length === 0 && (
          <p style={{ color: "var(--pw-color-text-muted)", fontSize: "0.9rem" }}>Keine Quellen gefunden.</p>
        )}
        {items?.map((s) => (
          <Link
            key={s.id}
            to={`/library/${s.id}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.8rem",
              padding: "0.9rem 1rem",
              background: "var(--pw-color-surface-soft)",
              border: "1px solid var(--pw-color-surface-soft-border)",
              borderRadius: "var(--pw-radius-md)",
              color: "var(--pw-color-text)",
              textDecoration: "none"
            }}
          >
            <span
              style={{
                flexShrink: 0,
                fontSize: "9px",
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                padding: "3px 7px",
                borderRadius: "4px",
                background: "var(--pw-color-accent-bg)",
                color: "var(--pw-color-accent-strong)"
              }}
            >
              {TYPE_LABELS[s.type]}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: "block", fontWeight: 600, fontSize: "0.95rem" }}>{s.title}</span>
              {s.author && <span style={{ display: "block", fontSize: "0.78rem", color: "var(--pw-color-text-muted)" }}>{s.author}</span>}
            </span>
            {member.role === "admin" && s.publishStatus !== "published" && (
              <span style={{ fontSize: "0.72rem", color: "var(--pw-color-warning)" }}>{s.publishStatus === "draft" ? "Entwurf" : "Zurückgezogen"}</span>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}
